import React from "react";
import { createPortal } from "react-dom";
import { ArrowUp, BookOpen, CircleHelp, Copy, FileText, Plus, Route, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { useLocation } from "react-router";
import useAccess from "../../hooks/useAccess.jsx";
import { ACCESS_PERMISSION_KEYS, hasPermission, isAdminRole } from "../../lib/accessControl.js";
import { askEpi, fetchEpiReadyGuides, fetchEpiStatus, sendEpiFeedback } from "../../lib/assistantApi.js";
import EpiCharacter from "./EpiCharacter.jsx";
import EpiAvatar from "./EpiAvatar.jsx";
import EpiSettings from "./EpiSettings.jsx";
import EpiReadyGuides from "./EpiReadyGuides.jsx";
import EpiDocument, { EpiAnswer } from "./EpiDocument.jsx";
import { EpiCapture, EpiPointer, EpiShownOnScreen } from "./EpiShow.jsx";
import { buildEpiContext, describeEpiContext, epiDocumentText, findEpiTarget } from "./epiAnswerBlocks.js";
import "./epi.css";

// En dessous, la fenêtre de Lumi prend tout l'écran : la capture du Guide remplace la flèche.
const DOCK_MIN_WIDTH = 900;
const EMPTY_CONVERSATION = { context: null, error: "", phase: "idle", question: "", pendingQuestion: "", turns: [] };

const CAPABILITIES = [
  { icon: CircleHelp, label: "Expliquer", question: "Que fait « Optimiser ce camion » ?" },
  { icon: Route, label: "Guider", question: "Explique-moi comment faire un dispatch." },
  { icon: FileText, label: "Rédiger", question: "Rédige-moi un guide sur le mélange de produits, en PDF ou en Word." },
];
const PAGE_EXAMPLES = {
  "/results": ["Pourquoi « Réinitialiser le planning » est-il grisé ?", "Comment fonctionne le mélange de produits ?", "Que veut dire « À revalider » à côté de la date ?"],
  "/data-manager": ["Où est la vitesse des camions ?", "À quoi sert la colonne « Produit associé » ?", "Comment ajouter un chauffeur ?"],
  "/import": ["Je ne vois pas mes nouvelles commandes après l'import. Pourquoi ?", "Quels fichiers puis-je importer ?"],
  "/consolidation": ["Que veut dire le petit crochet vert sur la carte du chauffeur ?", "Comment valider le relevé d'un chauffeur ?"],
};
const DEFAULT_EXAMPLES = ["Comment fonctionne le mélange de produits ?", "Pourquoi « Réinitialiser le planning » est-il grisé ?"];

// Plusieurs choix se lisent comme des boutons radio : on coche, puis on pose la question.
function EpiFollowUpChoice({ disabled, items, onAsk }) {
  const [chosen, setChosen] = React.useState("");
  const titleId = React.useId();
  return (
    <form className="epi-choice" onSubmit={(event) => { event.preventDefault(); if (chosen) onAsk(chosen); }}>
      <div className="epi-choice__head">
        <p className="epi-choice__title" id={titleId}>Que voulez-vous préciser ?</p>
        <button className="epi-choice__ask" disabled={disabled || !chosen} type="submit">Poser cette question</button>
      </div>
      <div aria-labelledby={titleId} role="radiogroup">
        {items.map((item) => (
          <label className="epi-choice__item" key={item}>
            <input checked={chosen === item} disabled={disabled} name={titleId} onChange={() => setChosen(item)} type="radio" value={item} />{item}
          </label>
        ))}
      </div>
    </form>
  );
}

function EpiDialog({ canManage, conversation, guides, longWait, onAsk, onClose, onFeedback, onNewConversation, onQuestionChange, pathname, status }) {
  const inputRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const bodyRef = React.useRef(null);
  const { context, error, phase, question, pendingQuestion, turns } = conversation;
  const result = turns.at(-1);
  const show = phase === "thinking" ? null : result?.show || null;
  const [target, setTarget] = React.useState(null);
  const [replay, setReplay] = React.useState(0);
  React.useEffect(() => {
    const element = show && window.innerWidth >= DOCK_MIN_WIDTH ? findEpiTarget(show, document, pathname) : null;
    const rect = element?.getBoundingClientRect();
    // La fenêtre se range du côté opposé à l'élément pour ne pas le cacher.
    setTarget(element ? { element, side: rect.left + rect.width / 2 > window.innerWidth / 2 ? "left" : "right" } : null);
  }, [pathname, show]);
  const contextLabel = describeEpiContext(context);
  const isThinking = phase === "thinking";
  const unavailableMessage = status && !status.available ? status.message : "";
  React.useEffect(() => {
    if (bodyRef.current) {
      // Après une réponse, sa question reste en haut : la réponse se lit depuis son début, avant les suites.
      const turn = phase === "answered" && !error ? bodyRef.current.querySelector(".epi-turn:last-of-type") : null;
      bodyRef.current.scrollTop = turn
        ? turn.getBoundingClientRect().top - bodyRef.current.getBoundingClientRect().top + bodyRef.current.scrollTop
        : bodyRef.current.scrollHeight;
    }
    if (!isThinking) inputRef.current?.focus();
  }, [error, isThinking, longWait, turns.length]);
  React.useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") { event.stopPropagation(); onClose(); }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);
  const submit = (event) => {
    event?.preventDefault();
    if (!isThinking && !unavailableMessage && question.trim().length >= 3) {
      onAsk(question.trim());
    }
  };
  return createPortal(
    <div
      className={target ? `epi-overlay epi-overlay--docked epi-overlay--dock-${target.side}` : "epi-overlay"}
      data-epi-dialog="true"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
      role="presentation"
    >
      {target ? <EpiPointer panelRef={panelRef} replay={replay} target={target.element} /> : null}
      <section aria-label="Demander à Lumi" aria-modal="true" className="epi-panel" ref={panelRef} role="dialog">
        <div className="epi-screen">
          {isThinking ? <div aria-hidden="true" className="epi-run" /> : null}
          <div className="epi-header">
            <EpiAvatar size={24} />
            <div className="epi-who">
              <h2 className="epi-title">Demander à Lumi</h2>
              <p className="epi-role">Répartitrice virtuelle{contextLabel ? <> · {contextLabel.prefix} <b>{contextLabel.value}</b>{contextLabel.suffix}</> : null}</p>
            </div>
            <div className="epi-header__actions">
              {guides.length && !target ? <EpiReadyGuides guides={guides} /> : null}
              <button className="inline-flex h-7 items-center gap-1.5 rounded-[4px] border border-slate-300 bg-white px-2 py-0 text-xs font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-accent"
                onClick={() => { onNewConversation(); inputRef.current?.focus(); }} type="button"><Plus aria-hidden="true" size={14} />Nouvelle conversation</button>
              {canManage && !target ? <EpiSettings disabled={isThinking} /> : null}
              <button aria-label="Fermer (Échap)" className="epi-icon" onClick={onClose} title="Fermer (Échap)" type="button"><X aria-hidden="true" size={16} /></button>
            </div>
          </div>
          <div aria-live="polite" className="epi-body" ref={bodyRef}>
            {turns.map((turn) => (
              <section aria-label="Échange avec Lumi" className="epi-turn" key={turn.id}>
                <p className="epi-user-question"><span className="sr-only">Vous : </span>{turn.question}</p>
                <div className="epi-reply">
                  <EpiAvatar />
                  <div className="epi-reply__body">
                    {turn.document ? <EpiDocument document={turn.document} questionId={turn.id} /> : <EpiAnswer muted={turn.status !== "answered"} text={turn.answer} />}
                    {turn === result && show ? (target
                      ? <EpiShownOnScreen label={show.label} onReplay={() => setReplay((value) => value + 1)} />
                      : <EpiCapture elsewhere={pathname !== show.page.split("?")[0]} onClose={onClose} show={show} />) : null}
                    <div className="epi-footer">
                      <div className="epi-sources">
                        {turn.sources.map((source) => <span className="epi-source" key={source.slug} title={`Guide : ${source.title}`}><BookOpen aria-hidden="true" size={13} />{source.title}</span>)}
                      </div>
                      {turn.status === "answered" ? (
                        <div className="epi-actions">
                          <button aria-label="Copier la réponse" className="epi-tool" onClick={() => navigator.clipboard?.writeText(turn.document ? epiDocumentText(turn.document) : turn.answer)} title="Copier la réponse" type="button"><Copy aria-hidden="true" size={14} /></button>
                          <button aria-label="Réponse utile" aria-pressed={turn.feedback === "up"} className="epi-tool" onClick={() => onFeedback(turn.id, "up")} title="Réponse utile" type="button"><ThumbsUp aria-hidden="true" size={14} /></button>
                          <button aria-label="Réponse pas utile" aria-pressed={turn.feedback === "down"} className="epi-tool" onClick={() => onFeedback(turn.id, "down")} title="Réponse pas utile" type="button"><ThumbsDown aria-hidden="true" size={14} /></button>
                        </div>
                      ) : null}
                    </div>
                    {turn.feedback === "down" ? <p className="epi-hint">Merci. La question est transmise à l'administrateur.</p> : null}
                    {turn === result && !isThinking && turn.follow_ups.length ? (
                      turn.follow_ups.length > 1
                        ? <EpiFollowUpChoice disabled={Boolean(unavailableMessage)} items={turn.follow_ups} key={turn.id} onAsk={onAsk} />
                        : <div aria-label="Poursuivre cet échange" className="epi-suggestions" role="group">
                          <button className="epi-suggestion" disabled={Boolean(unavailableMessage)} onClick={() => onAsk(turn.follow_ups[0])} title={turn.follow_ups[0]} type="button">{turn.follow_ups[0]}</button>
                        </div>
                    ) : null}
                  </div>
                </div>
              </section>
            ))}
            {pendingQuestion ? <p className="epi-user-question"><span className="sr-only">Vous : </span>{pendingQuestion}</p> : null}
            {unavailableMessage ? <p className="epi-error">{unavailableMessage}</p>
              : isThinking ? (
                <div role="status"><div className="epi-thinking"><EpiAvatar /><span aria-hidden="true" className="epi-dots"><i /><i /><i /></span>Lumi cherche dans le Guide du SaaS…</div>
                  {longWait ? <p className="epi-hint">Un guide complet peut prendre quelques minutes.</p> : null}
                </div>
              ) : error ? <p className="epi-error" role="alert">{error}</p>
              : !turns.length ? (
                <>
                  <h3 className="epi-hello">Bonjour ! Que voulez-vous comprendre ?</h3>
                  <p className="epi-hint">Je réponds à partir du Guide du SaaS. Je ne touche jamais au dispatch.</p>
                  <div className="epi-cards">
                    {CAPABILITIES.map(({ icon: Icon, label, question: example }) => (
                      <button className="epi-card" key={label} onClick={() => onAsk(example)} type="button"><span className="epi-card__tag"><Icon aria-hidden="true" size={16} />{label}</span>{example}</button>
                    ))}
                  </div>
                  <div className="epi-examples">
                    <p className="epi-examples__title">Exemples pour cette page</p>
                    {(PAGE_EXAMPLES[pathname] || DEFAULT_EXAMPLES).map((item) => (
                      <button className="epi-example" key={item} onClick={() => onAsk(item)} type="button"><CircleHelp aria-hidden="true" size={16} />{item}</button>
                    ))}
                  </div>
                </>
              ) : null}
          </div>
          <form className="epi-composer" onSubmit={submit}>
            <div className="epi-composer__controls">
              <textarea
                aria-label="Votre question pour Lumi"
                className="epi-question__input"
                disabled={isThinking || Boolean(unavailableMessage)}
                maxLength={1000}
                onChange={(event) => onQuestionChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) submit(event);
                }}
                placeholder={turns.length ? "Ajoutez une précision ou une question de suivi…" : "Posez votre question…"}
                ref={inputRef}
                rows={1}
                value={question}
              />
              <button aria-label="Envoyer" className="epi-send" disabled={isThinking || Boolean(unavailableMessage) || question.trim().length < 3} title="Envoyer (Entrée) · Maj + Entrée pour changer de ligne" type="submit"><ArrowUp aria-hidden="true" size={18} /></button>
            </div>
          </form>
        </div>
      </section>
      {target ? null : <EpiCharacter phase={phase} />}
    </div>,
    document.body,
  );
}

export default function AskEpiButton({ compact = false, pageTitle = "" }) {
  const { access } = useAccess();
  const location = useLocation();
  const allowed = hasPermission(access, ACCESS_PERMISSION_KEYS.assistantUse);
  const [open, setOpen] = React.useState(false);
  const [status, setStatus] = React.useState(null);
  const [conversation, setConversation] = React.useState(EMPTY_CONVERSATION);
  const [longWait, setLongWait] = React.useState(false);
  const [guides, setGuides] = React.useState([]);
  const requestRef = React.useRef(0);

  React.useEffect(() => {
    setLongWait(false);
    if (conversation.phase !== "thinking") return undefined;
    const timer = window.setTimeout(() => setLongWait(true), 15000);
    return () => window.clearTimeout(timer);
  }, [conversation.phase]);

  React.useEffect(() => {
    if (!allowed) {
      return undefined;
    }
    const onKeyDown = (event) => {
      // Une seule barre réagit, même si deux sont montées.
      if ((event.ctrlKey || event.metaKey) && event.key?.toLowerCase() === "k" && !event.defaultPrevented) {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [allowed]);

  React.useEffect(() => {
    if (open && allowed && !status) {
      fetchEpiStatus().then(setStatus).catch((failure) => setStatus({ available: false, message: failure.message }));
    }
  }, [allowed, open, status]);

  // Seuls les guides prêts de la page ouverte ; sans guide, pas de bouton.
  React.useEffect(() => {
    if (!open || !allowed) return undefined;
    let active = true;
    fetchEpiReadyGuides(location.pathname).then((value) => { if (active) setGuides(Array.isArray(value) ? value : []); })
      .catch(() => { if (active) setGuides([]); });
    return () => { active = false; };
  }, [allowed, open, location.pathname]);

  if (!allowed) {
    return null;
  }

  const context = buildEpiContext(location, pageTitle);
  const ask = async (question) => {
    if (conversation.phase === "thinking") return;
    const request = ++requestRef.current;
    const previousQuestionIds = conversation.turns.map((turn) => turn.id);
    setConversation((current) => ({ ...current, context, error: "", phase: "thinking", question: "", pendingQuestion: question }));
    try {
      const result = await askEpi(question, context, previousQuestionIds);
      if (request === requestRef.current) setConversation((current) => ({ ...current, phase: "answered", pendingQuestion: "", turns: [...current.turns, { ...result, question }] }));
    } catch (failure) {
      if (request === requestRef.current) setConversation((current) => ({ ...current, phase: "error", error: failure.message, pendingQuestion: "", question }));
    }
  };
  const newConversation = () => {
    // Une réponse encore en vol ne doit pas réapparaître dans la nouvelle conversation.
    requestRef.current += 1;
    setConversation(EMPTY_CONVERSATION);
  };
  const giveFeedback = async (questionId, value) => {
    const request = requestRef.current;
    try {
      const result = await sendEpiFeedback(questionId, value);
      // La note reprend l'illustration et le document déjà reçus, sans nouvelle rédaction.
      setConversation((current) => ({ ...current, turns: current.turns.map((turn) => turn.id === questionId ? { ...result, show: turn.show, document: turn.document, kind: turn.kind } : turn) }));
    } catch (failure) {
      if (request === requestRef.current) setConversation((current) => ({ ...current, error: failure.message }));
    }
  };

  return (
    <>
      <button
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={compact ? "Demander à Lumi" : undefined}
        className="epi-ask-button"
        data-epi-ask-button="true"
        onClick={() => setOpen(true)}
        title="Demander à Lumi (Ctrl K)"
        type="button"
      >
        <EpiAvatar size={26} />
        {compact ? null : <>Demander à Lumi <kbd className="epi-ask-button__key">Ctrl K</kbd></>}
      </button>
      {open ? (
        <EpiDialog
          canManage={isAdminRole(access)}
          conversation={{ ...conversation, context: conversation.context || context }}
          guides={guides}
          longWait={longWait}
          onAsk={ask}
          onClose={() => setOpen(false)}
          onFeedback={giveFeedback}
          onNewConversation={newConversation}
          onQuestionChange={(question) => setConversation((current) => ({ ...current, question }))}
          pathname={location.pathname}
          status={status}
        />
      ) : null}
    </>
  );
}
